// AI V2 wire contract — the ONE definition shared byte-for-byte by the iOS / web client
// (src/backend/aiClient.ts) and the Supabase edge function (supabase/functions/ai-v2). Pure
// TypeScript: no imports, no runtime globals, so it runs in the browser, in Deno and in Node.
//
// Security architecture (Owner task book 2026-09-04 Round 2 §J): the client never holds a provider
// key. It sends the signed-in user's Supabase JWT to /functions/v1/ai-v2; the function validates
// the user, enforces the limits below, calls the provider with the server-side secret and answers
// with the structured result. Every response — success or failure — is one JSON object of the
// shapes below; the client never sees raw provider errors.
//
// 2026-09-14 (Final Owner Decision, ratification CA-4 / CA-5 / CA-14): the focused re-analysis
// ("ask AI again" on one suggestion) is gone — the only photo request is the AREA scan; the report
// wording request carries the row's AI observations and advanced review as SUPPORTING context and no
// inspector instruction field.
export const AI_V2_PROTOCOL_VERSION = 1;
export const AI_V2_FUNCTION_PATH = '/functions/v1/ai-v2';

// Limits enforced by the function (and respected by the client before it uploads).
export const AI_V2_LIMITS = Object.freeze({
  maxPhotos: 24,
  // Base64 characters per photo (~6 MB of JPEG).
  maxPhotoBase64Chars: 8_000_000,
  // Whole request body, bytes.
  maxBodyBytes: 40_000_000,
  maxNoteChars: 4000,
  maxChecklistItems: 200,
  maxResults: 40
});

export type AiV2Locale = 'zh-CN' | 'en-US';
export type AiV2MediaType = 'image/jpeg' | 'image/png' | 'image/webp';
export const AI_V2_MEDIA_TYPES: readonly AiV2MediaType[] = Object.freeze(['image/jpeg', 'image/png', 'image/webp']);

export type AiV2ResultType = 'existingItem' | 'suggestedItem' | 'otherIssue' | 'needsClarification' | 'noAction';
export const AI_V2_RESULT_TYPES: readonly AiV2ResultType[] = Object.freeze(['existingItem', 'suggestedItem', 'otherIssue', 'needsClarification', 'noAction']);

export type AiV2Confidence = 'low' | 'medium' | 'high';

// Responsible Trade closed set (mirrors src/domain/responsibleTrade.ts; unknown values fail closed to null).
// 2026-10-19 (Owner: 「一定要写明是哪类专业人员」): the whole offered set except the NYS Licensed Mold Assessor,
// which an AI observation never earns (src/domain/responsibleTrade.ts AI_GUARDED_TRADES), so a result that is
// not on a checklist row can still name the siding, masonry, paving … contractor instead of "a professional".
export const AI_V2_TRADES: readonly string[] = Object.freeze([
  'licensedElectrician',
  'licensedPlumbingProfessional',
  'qualifiedHvacContractor',
  'structuralEngineer',
  'qualifiedRoofingContractor',
  'licensedPestProfessional',
  'qualifiedGeneralContractor',
  'qualifiedMasonryContractor',
  'qualifiedSidingContractor',
  'qualifiedWindowDoorContractor',
  'qualifiedGarageDoorTechnician',
  'qualifiedServiceTechnician',
  'qualifiedChimneyProfessional',
  'qualifiedInsulationContractor',
  'qualifiedWaterproofingContractor',
  'qualifiedDrainageGradingContractor',
  'qualifiedPavingContractor',
  'other'
]);

export interface AiV2Photo {
  id: string;
  mediaType: AiV2MediaType;
  base64: string;
  // The inspector's photo note (may be '').
  note: string;
  capturedAt: number | null;
}

export interface AiV2ChecklistItem {
  id: string;
  // Stored title (App Language at creation) and the English template title when known.
  title: string;
  titleEn: string | null;
  hint: string;
  status: string;
  // 2026-10-19 (Owner's 8 points, #2): the checklist template row this item was made from (src/domain/
  // checklistTemplates.ts ids such as 'kit-gfci'), so the prompt can list what a sound house has at that
  // row (ai-v2 core/prompt.ts EXPECTED_COMPONENTS). Additive on the wire: absent / null = unknown.
  templateId?: string | null;
  // 2026-10-19 (Owner: 「一定要写明是哪类专业人员」): the professional the app's rules library names for this row
  // (src/domain/inspectionRules.ts ROW_TRADES), as a trade code of AI_V2_TRADES; null = the row names none
  // (an observation / method row); absent = the app did not say. The prompt prints its name on the row so
  // every recommendation names that kind of professional, never 「专业人员」.
  trade?: string | null;
}

// 2026-10-18 (Owner: 「AI 解读分析有什么缺陷」, 56 Helmig St check): the house systems the inspector
// RECORDED, so the model reasons from facts instead of inventing a water heater's TPR valve or a
// bedroom heater. Every field is a recorded code or null = not recorded; the prompt tells the model
// that a system not listed here and not visible in the photos does not exist for it. Additive on the
// wire: an older server ignores the block, an older app never sends it.
export interface AiV2HouseContext {
  // Property facts (src/domain/descriptors.ts #9 / #10 / #11): 'gas' | 'oil' | 'electric' | 'propane' | 'other' | 'unknown'.
  heatingFuel: string | null;
  // The heating equipment / distribution the inspector typed or chose on the HVAC rows ('' never travels).
  heatingEquipment: string | null;
  heatingDistribution: string | null;
  // The water heater's energy source chosen on its data-plate row.
  waterHeaterEnergySource: string | null;
  // 'public' | 'private' | 'unknown'.
  waterSupplySource: string | null;
  wasteDisposalSource: string | null;
  // 'full' | 'partial' | 'crawlspace' | 'slab' | 'none' and 'unfinished' | 'finished' | 'partial' | 'mixed' | 'unknown'.
  basementType: string | null;
  basementFinish: string | null;
}

export const AI_V2_HOUSE_FIELDS: readonly (keyof AiV2HouseContext)[] = Object.freeze(['heatingFuel', 'heatingEquipment', 'heatingDistribution', 'waterHeaterEnergySource', 'waterSupplySource', 'wasteDisposalSource', 'basementType', 'basementFinish']);

export interface AiV2AreaContext {
  areaId: string;
  areaName: string;
  areaNameEn: string | null;
  areaKind: string;
  floor: number | null;
  propertyType: string | null;
  yearBuilt: number | null;
  // Optional (2026-10-18): absent / null = the app did not send the house systems.
  house?: AiV2HouseContext | null;
}

// 2026-10-18: what ONE result concludes, as a closed value the app can show and act on instead of
// parsing prose (「状况良好」 is forbidden; 「照片未见明显异常」 is a claim about the photos, not the house):
//   defect             a visible condition worth recording
//   noVisibleDefect    nothing abnormal is visible in these photos — says nothing about what was not photographed
//   fieldTestRequired  the item must be operated / tested on site; photographs cannot settle it
//   unclear            the model cannot tell what it is looking at (needsClarification)
export type AiV2Verdict = 'defect' | 'noVisibleDefect' | 'fieldTestRequired' | 'unclear';
export const AI_V2_VERDICTS: readonly AiV2Verdict[] = Object.freeze(['defect', 'noVisibleDefect', 'fieldTestRequired', 'unclear']);

export function isAiV2Verdict(v: unknown): v is AiV2Verdict {
  return typeof v === 'string' && (AI_V2_VERDICTS as readonly string[]).includes(v);
}

export interface AiV2Result {
  id: string;
  type: AiV2ResultType;
  itemId: string | null;
  title: string;
  headline: string;
  observation: string;
  concern: string;
  guidance: string;
  checkSteps: string[];
  recommendation: string;
  responsibleTrade: string | null;
  // 2026-09-14 (Owner device test: "中文版观察和建议全是英文"): the two report sentences rendered
  // again in the UI locale for the inspector's screen; null when the two locales are the same.
  observationUi?: string | null;
  recommendationUi?: string | null;
  confidence: AiV2Confidence;
  // 2026-10-18: the closed conclusion (see AiV2Verdict). Optional on the wire: an answer from an older
  // server has none and the client derives it from the type and the text (deriveAiV2Verdict).
  verdict?: AiV2Verdict | null;
  question: string | null;
  scopeNote: string | null;
  photoIds: string[];
}

export interface AiV2AreaRequest {
  protocolVersion: typeof AI_V2_PROTOCOL_VERSION;
  kind: 'area';
  requestId: string;
  uiLocale: AiV2Locale;
  reportLocale: AiV2Locale;
  area: AiV2AreaContext;
  checklist: AiV2ChecklistItem[];
  // The Area overall note (区域整体备注) — material context for the whole analysis.
  scanNote: string;
  photos: AiV2Photo[];
  // Which inspection this analysis belongs to, so the server can charge it to that inspection's own
  // AI allowance when a single purchase paid for it (Owner decision 2026-09-11 D-5). Optional on the
  // wire: a build older than the billing round omits it and is charged to the account pool only.
  inspectionId?: string | null;
  // 2026-10-19 (Owner item 31): the photo checklist's answers. Optional on the wire (older builds omit it).
  photoGuide?: AiV2PhotoGuide | null;
}

// The inspector's answers to the area's photo checklist: parts they said are NOT here (with the checklist row
// each belongs to, when the area has it) and the usual views nobody answered.
export interface AiV2PhotoGuide {
  absent: { itemId: string | null; name: string }[];
  notTaken: string[];
}

export function checkAiV2PhotoGuide(raw: unknown): AiV2PhotoGuide | null {
  if (!isRec(raw)) return null;
  const name = (v: unknown) => (typeof v === 'string' && v.trim().length > 0 ? v.trim().slice(0, 120) : null);
  const absent = Array.isArray(raw.absent)
    ? raw.absent.flatMap((a) => {
        if (!isRec(a)) return [];
        const n = name(a.name);
        return n ? [{ itemId: typeof a.itemId === 'string' && a.itemId.length > 0 && a.itemId.length <= 80 ? a.itemId : null, name: n }] : [];
      }).slice(0, 40)
    : [];
  const notTaken = Array.isArray(raw.notTaken) ? raw.notTaken.flatMap((v) => { const n = name(v); return n ? [n] : []; }).slice(0, 40) : [];
  return absent.length === 0 && notTaken.length === 0 ? null : { absent, notTaken };
}

// Requests that carry photographs: the area scan only.
export type AiV2Request = AiV2AreaRequest;

// The report wording of ONE area's recorded rows — no photographs travel. The facts the sentences are
// written from, in the order of authority the Final Owner Decision fixes (CA-14): the inspector's
// note, the inspector's result, the AI observations / advanced review, the other recorded facts.
// The professional and the check method are the rules library's, fixed on the device.
export interface AiV2WordingRow {
  itemId: string;
  title: string;
  titleEn: string | null;
  hint: string;
  result: 'checkedOk' | 'issueFound';
  inspectorNote: string;
  photoNotes: string[];
  // Every AI observation the row carries (report language), supporting context only.
  aiObservations: string[];
  // The row's advanced AI review summary, supporting context only ('' = none).
  advancedReview: string;
  // The professional the rules library names for this row, as a report-language label ('' = none).
  responsibleProfessional: string;
  // 2026-10-19: the same professional as the inspector's screen label (Chinese), so the zh sentence names the
  // kind too (「建议由电工评估」, never 「专业人员」). Optional and additive; '' / absent = not given.
  responsibleProfessionalZh?: string;
  // 2026-09-13 (TW-3 / TW-2): the recorded operational-test fact and the row's scope class; absent = false.
  operationalTestPerformed?: boolean;
  supplemental?: boolean;
  // Owner device test 2026-09-21 — both optional and additive (protocol version unchanged: a server
  // that does not know them ignores them, an app that does not send them gets the behaviour of before).
  //   inspectorWording   the report sentence the INSPECTOR wrote for this row, in the language they
  //                      wrote it (most inspectors write Chinese). Not an instruction to the model: it
  //                      is the text to RENDER — same facts, same meaning — as professional report
  //                      language in both languages. '' / absent = none.
  //   adoptObservations  the inspector read the row's AI observations and asked for the sentence to be
  //                      written FROM them: their factual content counts as the inspector's own
  //                      observation for this row. The inspector-selected result still rules.
  inspectorWording?: string;
  inspectorWordingLocale?: AiV2Locale;
  adoptObservations?: boolean;
}

export interface AiV2WordingRequest {
  protocolVersion: typeof AI_V2_PROTOCOL_VERSION;
  kind: 'reportWording';
  requestId: string;
  uiLocale: AiV2Locale;
  reportLocale: AiV2Locale;
  area: AiV2AreaContext;
  scanNote: string;
  rows: AiV2WordingRow[];
  inspectionId?: string | null;
}

export const AI_V2_WORDING_LIMITS = Object.freeze({ maxRows: 60, maxPhotoNotes: 12, maxObservations: 8, maxSentenceChars: 900 });

// ---------- the photo-pool sort (Owner device test 2026-10-05: 「AI 分析照片池」) ----------
//
// The photos imported into the pool in one batch are SORTED first: for each photo, the area of the
// house it shows and the checklist rows of that area it is evidence for. The sort only places photos —
// it judges nothing; the area scan (kind 'area', unchanged) then analyzes each area's photos. Additive
// on the wire: an app that never sends it is unaffected, a server that does not know it refuses it as
// badRequest.
export interface AiV2SortRow {
  id: string;
  title: string;
  titleEn: string | null;
}

export interface AiV2SortArea {
  areaId: string;
  areaName: string;
  areaNameEn: string | null;
  areaKind: string;
  floor: number | null;
  rows: AiV2SortRow[];
}

export interface AiV2SortRequest {
  protocolVersion: typeof AI_V2_PROTOCOL_VERSION;
  kind: 'poolSort';
  requestId: string;
  uiLocale: AiV2Locale;
  reportLocale: AiV2Locale;
  propertyType: string | null;
  yearBuilt: number | null;
  areas: AiV2SortArea[];
  photos: AiV2Photo[];
  inspectionId?: string | null;
}

export const AI_V2_SORT_LIMITS = Object.freeze({ maxPhotos: 24, maxAreas: 80, maxRows: 1500, maxRowsPerPhoto: 3 });

// Where ONE photo goes: an area of the house (null = the AI could not place it) and the rows of that
// area it is evidence for (possibly none).
export interface AiV2Placement {
  photoId: string;
  areaId: string | null;
  itemIds: string[];
  confidence: AiV2Confidence;
}

// Everything the function accepts: the photo request, the report wording and the pool sort.
export type AiV2AnyRequest = AiV2Request | AiV2WordingRequest | AiV2SortRequest;

export interface AiV2Usage {
  provider: 'anthropic';
  model: string;
  inputTokens: number;
  outputTokens: number;
  durationMs: number;
  requestId: string;
  stopReason: string | null;
}

// What the AI allowance looks like AFTER this request (Owner decision 2026-09-11 X-7: the server
// keeps the books and the app displays the server's number). Present on every answer the quota gate
// touched — success or refusal — so the AI card can show the truth without a second round trip.
export interface AiV2Quota {
  // Account-level allowance left: period pool + add-on packs, in micro-USD.
  remainingMicros: number;
  // The inspection's own allowance left (a single purchase carries one), or null when this
  // inspection has none.
  inspectionRemainingMicros: number | null;
  // floor(remainingMicros / one inspection's estimated cost) — the "about N inspections left" figure.
  remainingInspections: number;
  planActive: boolean;
  // Only an active monthly / annual subscriber may buy the add-on pack (D-3).
  canBuyAddOn: boolean;
}

export interface AiV2SuccessResponse {
  ok: true;
  protocolVersion: typeof AI_V2_PROTOCOL_VERSION;
  kind: 'area';
  requestId: string;
  // One-paragraph area summary in the UI locale.
  summary: string;
  results: AiV2Result[];
  usage: AiV2Usage;
  warnings: string[];
  quota?: AiV2Quota;
}

export type AiV2ErrorCode =
  | 'unauthenticated'
  | 'badRequest'
  | 'tooLarge'
  | 'tooManyImages'
  | 'unsupportedImage'
  | 'timeout'
  | 'rateLimited'
  | 'providerError'
  | 'providerRefused'
  | 'malformedResponse'
  | 'unavailable'
  // The AI allowance is used up (Owner decision 2026-09-11 D-3 / D-4): its own code, so the AI card
  // says exactly that and can offer the add-on pack instead of showing a generic failure.
  | 'quotaExhausted'
  // The server could not verify the allowance (billing key / ledger missing or unreachable). Paid
  // AI fails CLOSED here: an unverifiable allowance is never spent.
  | 'billingUnavailable';

export interface AiV2ErrorResponse {
  ok: false;
  protocolVersion: typeof AI_V2_PROTOCOL_VERSION;
  requestId: string | null;
  code: AiV2ErrorCode;
  // Human-readable, never a raw provider message with secrets or image bytes.
  message: string;
  retryable: boolean;
  quota?: AiV2Quota;
}

export interface AiV2WordingSentence {
  itemId: string;
  en: string;
  zh: string;
}

export interface AiV2WordingSuccessResponse {
  ok: true;
  protocolVersion: typeof AI_V2_PROTOCOL_VERSION;
  kind: 'reportWording';
  requestId: string;
  rows: AiV2WordingSentence[];
  areaSummary: { en: string; zh: string };
  usage: AiV2Usage;
  warnings: string[];
  quota?: AiV2Quota;
}

export interface AiV2SortSuccessResponse {
  ok: true;
  protocolVersion: typeof AI_V2_PROTOCOL_VERSION;
  kind: 'poolSort';
  requestId: string;
  // One placement per photo of the request, in the request's order.
  placements: AiV2Placement[];
  usage: AiV2Usage;
  warnings: string[];
  quota?: AiV2Quota;
}

// What a photo request answers / what the wording request answers / what the pool sort answers /
// everything the wire carries.
export type AiV2PhotoResponse = AiV2SuccessResponse | AiV2ErrorResponse;
export type AiV2WordingResponse = AiV2WordingSuccessResponse | AiV2ErrorResponse;
export type AiV2SortResponse = AiV2SortSuccessResponse | AiV2ErrorResponse;
export type AiV2Response = AiV2SuccessResponse | AiV2WordingSuccessResponse | AiV2SortSuccessResponse | AiV2ErrorResponse;

// HTTP status per error code (the function's contract; the client maps codes, not statuses).
export const AI_V2_ERROR_STATUS: Readonly<Record<AiV2ErrorCode, number>> = Object.freeze({
  unauthenticated: 401,
  badRequest: 400,
  tooLarge: 413,
  tooManyImages: 413,
  unsupportedImage: 415,
  timeout: 504,
  rateLimited: 429,
  providerError: 502,
  providerRefused: 502,
  malformedResponse: 502,
  unavailable: 503,
  quotaExhausted: 402,
  billingUnavailable: 503
});

export function aiV2Error(code: AiV2ErrorCode, message: string, requestId: string | null = null, retryable?: boolean): AiV2ErrorResponse {
  // quotaExhausted is NOT retryable: retrying spends nothing new, it only repeats the refusal. A
  // billing service that could not be reached is (the allowance may well be there).
  const defaultRetryable =
    code === 'timeout' || code === 'rateLimited' || code === 'providerError' || code === 'malformedResponse' || code === 'unavailable' || code === 'billingUnavailable';
  return { ok: false, protocolVersion: AI_V2_PROTOCOL_VERSION, requestId, code, message, retryable: retryable ?? defaultRetryable };
}

// JSON schema the provider is constrained to (structured outputs). Kept next to the types so a
// test can pin that every schema property is a protocol field and vice versa.
export const AI_V2_OUTPUT_JSON_SCHEMA = Object.freeze({
  type: 'object',
  properties: {
    summary: { type: 'string' },
    results: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          type: { type: 'string', enum: [...AI_V2_RESULT_TYPES] },
          itemId: { type: ['string', 'null'] },
          title: { type: 'string' },
          headline: { type: 'string' },
          observation: { type: 'string' },
          concern: { type: 'string' },
          guidance: { type: 'string' },
          checkSteps: { type: 'array', items: { type: 'string' } },
          recommendation: { type: 'string' },
          observationUi: { type: ['string', 'null'] },
          recommendationUi: { type: ['string', 'null'] },
          // nullable enum as anyOf: the Messages API validator refuses enum + ['string','null'] (smoke 2026-09-04)
          responsibleTrade: { anyOf: [{ type: 'string', enum: [...AI_V2_TRADES] }, { type: 'null' }] },
          confidence: { type: 'string', enum: ['low', 'medium', 'high'] },
          verdict: { type: 'string', enum: [...AI_V2_VERDICTS] },
          question: { type: ['string', 'null'] },
          scopeNote: { type: ['string', 'null'] },
          photoIds: { type: 'array', items: { type: 'string' } }
        },
        required: ['type', 'itemId', 'title', 'headline', 'observation', 'concern', 'guidance', 'checkSteps', 'recommendation', 'observationUi', 'recommendationUi', 'responsibleTrade', 'confidence', 'verdict', 'question', 'scopeNote', 'photoIds'],
        additionalProperties: false
      }
    }
  },
  required: ['summary', 'results'],
  additionalProperties: false
});

// ---------- validation (shared) ----------

type Rec = Record<string, unknown>;

function isRec(v: unknown): v is Rec {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
}

function strArray(v: unknown, maxItems: number): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, maxItems) : [];
}

export function isAiV2Locale(v: unknown): v is AiV2Locale {
  return v === 'zh-CN' || v === 'en-US';
}

export function isAiV2MediaType(v: unknown): v is AiV2MediaType {
  return typeof v === 'string' && (AI_V2_MEDIA_TYPES as readonly string[]).includes(v);
}

export function isAiV2ResultType(v: unknown): v is AiV2ResultType {
  return typeof v === 'string' && (AI_V2_RESULT_TYPES as readonly string[]).includes(v);
}

// JSON schema of the report-wording answer (structured outputs).
export const AI_V2_WORDING_JSON_SCHEMA = Object.freeze({
  type: 'object',
  properties: {
    rows: {
      type: 'array',
      items: {
        type: 'object',
        properties: { itemId: { type: 'string' }, en: { type: 'string' }, zh: { type: 'string' } },
        required: ['itemId', 'en', 'zh'],
        additionalProperties: false
      }
    },
    areaSummary: { type: 'object', properties: { en: { type: 'string' }, zh: { type: 'string' } }, required: ['en', 'zh'], additionalProperties: false }
  },
  required: ['rows', 'areaSummary'],
  additionalProperties: false
});

// The provider's wording JSON → validated sentences: only rows that were asked for, trimmed, capped.
export function normalizeAiV2Wording(raw: unknown, allowedItemIds: ReadonlySet<string>): { rows: AiV2WordingSentence[]; areaSummary: { en: string; zh: string } } | null {
  if (!isRec(raw) || !Array.isArray(raw.rows)) return null;
  const rows: AiV2WordingSentence[] = [];
  const seen = new Set<string>();
  for (const r of raw.rows) {
    if (!isRec(r) || typeof r.itemId !== 'string' || !allowedItemIds.has(r.itemId) || seen.has(r.itemId)) continue;
    const en = str(r.en).trim().slice(0, AI_V2_WORDING_LIMITS.maxSentenceChars);
    const zh = str(r.zh).trim().slice(0, AI_V2_WORDING_LIMITS.maxSentenceChars);
    if (en.length === 0 && zh.length === 0) continue;
    seen.add(r.itemId);
    rows.push({ itemId: r.itemId, en, zh });
  }
  const summary = isRec(raw.areaSummary) ? { en: str(raw.areaSummary.en).trim().slice(0, AI_V2_WORDING_LIMITS.maxSentenceChars), zh: str(raw.areaSummary.zh).trim().slice(0, AI_V2_WORDING_LIMITS.maxSentenceChars) } : { en: '', zh: '' };
  return { rows, areaSummary: summary };
}

// JSON schema of the pool-sort answer (structured outputs).
export const AI_V2_SORT_JSON_SCHEMA = Object.freeze({
  type: 'object',
  properties: {
    placements: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          photoId: { type: 'string' },
          areaId: { type: ['string', 'null'] },
          itemIds: { type: 'array', items: { type: 'string' } },
          confidence: { type: 'string', enum: ['low', 'medium', 'high'] }
        },
        required: ['photoId', 'areaId', 'itemIds', 'confidence'],
        additionalProperties: false
      }
    }
  },
  required: ['placements'],
  additionalProperties: false
});

// The provider's sort JSON → validated placements: only photos that were sent (each once), only areas
// that were sent, only rows OF the placed area (a row of another area is dropped, never moved), at most
// AI_V2_SORT_LIMITS.maxRowsPerPhoto rows per photo. null when the shape is unusable.
export function normalizeAiV2Placements(raw: unknown, allowed: { photoIds: ReadonlySet<string>; rowsByArea: ReadonlyMap<string, ReadonlySet<string>> }): AiV2Placement[] | null {
  if (!isRec(raw) || !Array.isArray(raw.placements)) return null;
  const placements: AiV2Placement[] = [];
  const seen = new Set<string>();
  for (const p of raw.placements) {
    if (!isRec(p) || typeof p.photoId !== 'string' || !allowed.photoIds.has(p.photoId) || seen.has(p.photoId)) continue;
    seen.add(p.photoId);
    const rows = typeof p.areaId === 'string' ? allowed.rowsByArea.get(p.areaId) : undefined;
    const itemIds = rows ? [...new Set(strArray(p.itemIds, 20).filter((id) => rows.has(id)))].slice(0, AI_V2_SORT_LIMITS.maxRowsPerPhoto) : [];
    const confidence = p.confidence === 'low' || p.confidence === 'medium' || p.confidence === 'high' ? p.confidence : 'low';
    placements.push({ photoId: p.photoId, areaId: rows ? (p.areaId as string) : null, itemIds, confidence });
  }
  return placements;
}

// The photos of a request that carries photographs (the area scan and the pool sort): ids unique,
// a supported type, bytes present and within the size limit, the note within its limit.
function checkPhotos(raw: unknown, maxPhotos: number): { ok: true; photos: AiV2Photo[] } | { ok: false; code: AiV2ErrorCode; message: string } {
  if (!Array.isArray(raw) || raw.length === 0) return { ok: false, code: 'badRequest', message: 'at least one photo is required' };
  if (raw.length > maxPhotos) return { ok: false, code: 'tooManyImages', message: `at most ${maxPhotos} photos per request` };
  const photos: AiV2Photo[] = [];
  const seen = new Set<string>();
  for (const p of raw) {
    if (!isRec(p) || typeof p.id !== 'string' || p.id.length === 0 || seen.has(p.id)) return { ok: false, code: 'badRequest', message: 'photo id missing or duplicated' };
    if (!isAiV2MediaType(p.mediaType)) return { ok: false, code: 'unsupportedImage', message: `unsupported image type ${String(p.mediaType)}` };
    if (typeof p.base64 !== 'string' || p.base64.length === 0) return { ok: false, code: 'unsupportedImage', message: 'photo bytes missing' };
    if (p.base64.length > AI_V2_LIMITS.maxPhotoBase64Chars) return { ok: false, code: 'tooLarge', message: 'one photo exceeds the size limit' };
    const note = str(p.note);
    if (note.length > AI_V2_LIMITS.maxNoteChars) return { ok: false, code: 'badRequest', message: 'photo note too long' };
    seen.add(p.id);
    photos.push({ id: p.id, mediaType: p.mediaType, base64: p.base64, note, capturedAt: typeof p.capturedAt === 'number' ? p.capturedAt : null });
  }
  return { ok: true, photos };
}

function checkSortRequest(raw: Rec): AiV2RequestCheck {
  if (typeof raw.requestId !== 'string' || raw.requestId.length === 0 || raw.requestId.length > 80) return { ok: false, code: 'badRequest', message: 'requestId missing' };
  if (!isAiV2Locale(raw.uiLocale) || !isAiV2Locale(raw.reportLocale)) return { ok: false, code: 'badRequest', message: 'uiLocale / reportLocale must be zh-CN or en-US' };
  if (!Array.isArray(raw.areas) || raw.areas.length === 0 || raw.areas.length > AI_V2_SORT_LIMITS.maxAreas) return { ok: false, code: 'badRequest', message: 'areas missing or too many' };
  const areas: AiV2SortArea[] = [];
  const areaIds = new Set<string>();
  const rowIds = new Set<string>();
  for (const a of raw.areas) {
    if (!isRec(a) || typeof a.areaId !== 'string' || a.areaId.length === 0 || areaIds.has(a.areaId) || typeof a.areaName !== 'string' || !Array.isArray(a.rows)) return { ok: false, code: 'badRequest', message: 'area malformed' };
    areaIds.add(a.areaId);
    const rows: AiV2SortRow[] = [];
    for (const r of a.rows) {
      if (!isRec(r) || typeof r.id !== 'string' || r.id.length === 0 || rowIds.has(r.id) || typeof r.title !== 'string') return { ok: false, code: 'badRequest', message: 'row malformed' };
      rowIds.add(r.id);
      rows.push({ id: r.id, title: r.title.slice(0, 200), titleEn: strOrNull(r.titleEn) });
    }
    if (rowIds.size > AI_V2_SORT_LIMITS.maxRows) return { ok: false, code: 'badRequest', message: `at most ${AI_V2_SORT_LIMITS.maxRows} rows per request` };
    areas.push({ areaId: a.areaId, areaName: a.areaName.slice(0, 200), areaNameEn: strOrNull(a.areaNameEn), areaKind: str(a.areaKind, 'other'), floor: typeof a.floor === 'number' ? a.floor : null, rows });
  }
  const photos = checkPhotos(raw.photos, AI_V2_SORT_LIMITS.maxPhotos);
  if (!photos.ok) return photos;
  const inspectionId = typeof raw.inspectionId === 'string' && raw.inspectionId.length > 0 && raw.inspectionId.length <= 120 ? raw.inspectionId : null;
  return {
    ok: true,
    request: {
      protocolVersion: AI_V2_PROTOCOL_VERSION,
      kind: 'poolSort',
      requestId: raw.requestId,
      uiLocale: raw.uiLocale,
      reportLocale: raw.reportLocale,
      propertyType: strOrNull(raw.propertyType),
      yearBuilt: typeof raw.yearBuilt === 'number' ? raw.yearBuilt : null,
      areas,
      photos: photos.photos,
      inspectionId
    }
  };
}

function checkWordingRequest(raw: Rec): AiV2RequestCheck {
  if (typeof raw.requestId !== 'string' || raw.requestId.length === 0 || raw.requestId.length > 80) return { ok: false, code: 'badRequest', message: 'requestId missing' };
  if (!isAiV2Locale(raw.uiLocale) || !isAiV2Locale(raw.reportLocale)) return { ok: false, code: 'badRequest', message: 'uiLocale / reportLocale must be zh-CN or en-US' };
  if (!isRec(raw.area) || typeof raw.area.areaId !== 'string' || typeof raw.area.areaName !== 'string') return { ok: false, code: 'badRequest', message: 'area context missing' };
  if (!Array.isArray(raw.rows) || raw.rows.length === 0) return { ok: false, code: 'badRequest', message: 'at least one row is required' };
  if (raw.rows.length > AI_V2_WORDING_LIMITS.maxRows) return { ok: false, code: 'badRequest', message: `at most ${AI_V2_WORDING_LIMITS.maxRows} rows per request` };
  const scanNote = str(raw.scanNote);
  if (scanNote.length > AI_V2_LIMITS.maxNoteChars) return { ok: false, code: 'badRequest', message: 'note too long' };
  const rows: AiV2WordingRow[] = [];
  const seen = new Set<string>();
  for (const r of raw.rows) {
    if (!isRec(r) || typeof r.itemId !== 'string' || r.itemId.length === 0 || seen.has(r.itemId) || typeof r.title !== 'string') return { ok: false, code: 'badRequest', message: 'row malformed' };
    if (r.result !== 'checkedOk' && r.result !== 'issueFound') return { ok: false, code: 'badRequest', message: 'row result must be checkedOk or issueFound' };
    const inspectorNote = str(r.inspectorNote);
    if (inspectorNote.length > AI_V2_LIMITS.maxNoteChars) return { ok: false, code: 'badRequest', message: 'row note too long' };
    seen.add(r.itemId);
    rows.push({
      itemId: r.itemId,
      title: r.title,
      titleEn: strOrNull(r.titleEn),
      hint: str(r.hint).slice(0, 600),
      result: r.result,
      inspectorNote,
      photoNotes: strArray(r.photoNotes, AI_V2_WORDING_LIMITS.maxPhotoNotes).map((n) => n.slice(0, 600)),
      aiObservations: strArray(r.aiObservations, AI_V2_WORDING_LIMITS.maxObservations).map((n) => n.slice(0, 1200)),
      advancedReview: str(r.advancedReview).slice(0, 2000),
      responsibleProfessional: str(r.responsibleProfessional).slice(0, 120),
      responsibleProfessionalZh: str(r.responsibleProfessionalZh).slice(0, 120),
      operationalTestPerformed: r.operationalTestPerformed === true,
      supplemental: r.supplemental === true,
      // 2026-09-21: the inspector's own sentence (capped like a written sentence) and the adoption flag.
      inspectorWording: str(r.inspectorWording).trim().slice(0, AI_V2_WORDING_LIMITS.maxSentenceChars),
      inspectorWordingLocale: isAiV2Locale(r.inspectorWordingLocale) ? r.inspectorWordingLocale : 'zh-CN',
      adoptObservations: r.adoptObservations === true
    });
  }
  const area: AiV2AreaContext = {
    areaId: raw.area.areaId,
    areaName: raw.area.areaName,
    areaNameEn: strOrNull(raw.area.areaNameEn),
    areaKind: str(raw.area.areaKind, 'other'),
    floor: typeof raw.area.floor === 'number' ? raw.area.floor : null,
    propertyType: strOrNull(raw.area.propertyType),
    yearBuilt: typeof raw.area.yearBuilt === 'number' ? raw.area.yearBuilt : null,
    house: checkAiV2HouseContext(raw.area.house)
  };
  const inspectionId = typeof raw.inspectionId === 'string' && raw.inspectionId.length > 0 && raw.inspectionId.length <= 120 ? raw.inspectionId : null;
  return { ok: true, request: { protocolVersion: AI_V2_PROTOCOL_VERSION, kind: 'reportWording', requestId: raw.requestId, uiLocale: raw.uiLocale, reportLocale: raw.reportLocale, area, scanNote, rows, inspectionId } };
}

// The recorded house systems of an area request (2026-10-18). Each field is a short code or null;
// anything that is not a non-empty string under 80 characters reads as "not recorded". A block with
// nothing recorded is kept as null (the prompt then says so in one line).
export function checkAiV2HouseContext(raw: unknown): AiV2HouseContext | null {
  if (!isRec(raw)) return null;
  const code = (v: unknown): string | null => (typeof v === 'string' && v.trim().length > 0 && v.trim().length <= 80 ? v.trim() : null);
  const house: AiV2HouseContext = {
    heatingFuel: code(raw.heatingFuel),
    heatingEquipment: code(raw.heatingEquipment),
    heatingDistribution: code(raw.heatingDistribution),
    waterHeaterEnergySource: code(raw.waterHeaterEnergySource),
    waterSupplySource: code(raw.waterSupplySource),
    wasteDisposalSource: code(raw.wasteDisposalSource),
    basementType: code(raw.basementType),
    basementFinish: code(raw.basementFinish)
  };
  return AI_V2_HOUSE_FIELDS.some((key) => house[key] !== null) ? house : null;
}

// Phrases that mark a "nothing visible" or a "must be tested on site" conclusion in either language —
// what an older server's answer (no verdict field) is read by. Shared so the app and the function agree.
const NO_VISIBLE_DEFECT_RE = /未见明显异常|照片未见|未见异常|no visible defect|nothing abnormal (?:is )?visible|no defect(?:s)? (?:is |are |was |were )?visible|not visible in (?:the|these) photo/i;
const FIELD_TEST_RE = /需现场测试|照片无法判断|需要现场(?:操作|测试)|requires? on-site test|cannot be (?:judged|determined|assessed|confirmed) from (?:the |these )?photo|must be tested on site|needs? (?:an )?on-site test/i;

// The verdict of a result that carries none (an answer from before 2026-10-18): the type decides when
// it can, otherwise the report / screen sentences are read for the two marked conclusions. Never
// 'defect' for a noAction result, never anything but 'unclear' for a clarification.
export function deriveAiV2Verdict(input: { type: AiV2ResultType; observation: string; observationUi?: string | null; headline: string; guidance: string }): AiV2Verdict {
  if (input.type === 'needsClarification') return 'unclear';
  if (input.type === 'noAction') return 'noVisibleDefect';
  const text = [input.observation, input.observationUi ?? '', input.headline, input.guidance].join('\n');
  if (FIELD_TEST_RE.test(text)) return 'fieldTestRequired';
  if (NO_VISIBLE_DEFECT_RE.test(text)) return 'noVisibleDefect';
  return 'defect';
}

// Normalizes ONE raw result against the request it answers: unknown photo ids are dropped, an
// itemId outside the checklist is nulled, a type that no longer fits its ids is corrected
// (existingItem without a valid item becomes otherIssue), unknown trades fail closed to null.
export function normalizeAiV2Result(raw: unknown, allowed: { photoIds: ReadonlySet<string>; itemIds: ReadonlySet<string> }, id: string): AiV2Result | null {
  if (!isRec(raw)) return null;
  if (!isAiV2ResultType(raw.type)) return null;
  const itemId = typeof raw.itemId === 'string' && allowed.itemIds.has(raw.itemId) ? raw.itemId : null;
  let type: AiV2ResultType = raw.type;
  if (type === 'existingItem' && itemId === null) type = 'otherIssue';
  const photoIds = [...new Set(strArray(raw.photoIds, AI_V2_LIMITS.maxPhotos).filter((p) => allowed.photoIds.has(p)))];
  const trade = typeof raw.responsibleTrade === 'string' && AI_V2_TRADES.includes(raw.responsibleTrade) ? raw.responsibleTrade : null;
  const confidence = raw.confidence === 'low' || raw.confidence === 'medium' || raw.confidence === 'high' ? raw.confidence : 'low';
  const title = str(raw.title).trim();
  const headline = str(raw.headline).trim();
  if (title.length === 0 && headline.length === 0) return null;
  const observation = str(raw.observation).trim();
  const observationUi = strOrNull(str(raw.observationUi).trim());
  const guidance = str(raw.guidance).trim();
  // A clarification is always 'unclear' and a noAction always 'noVisibleDefect', whatever the model
  // wrote in the field; otherwise the model's verdict stands and a missing one is derived.
  const verdict: AiV2Verdict = type === 'needsClarification' ? 'unclear' : type === 'noAction' ? 'noVisibleDefect' : isAiV2Verdict(raw.verdict) ? raw.verdict : deriveAiV2Verdict({ type, observation, observationUi, headline: headline.length > 0 ? headline : title, guidance });
  return {
    id,
    type,
    itemId: type === 'existingItem' ? itemId : null,
    title: title.length > 0 ? title : headline,
    headline: headline.length > 0 ? headline : title,
    observation,
    concern: str(raw.concern).trim(),
    guidance,
    checkSteps: strArray(raw.checkSteps, 20).map((s) => s.trim()).filter((s) => s.length > 0),
    recommendation: str(raw.recommendation).trim(),
    responsibleTrade: trade,
    observationUi,
    recommendationUi: strOrNull(str(raw.recommendationUi).trim()),
    confidence,
    verdict,
    question: type === 'needsClarification' ? (strOrNull(raw.question) ?? headline) : strOrNull(raw.question),
    scopeNote: strOrNull(raw.scopeNote),
    photoIds
  };
}

// The provider's JSON → validated results. Never throws; returns null when the shape is unusable.
export function normalizeAiV2Output(raw: unknown, allowed: { photoIds: ReadonlySet<string>; itemIds: ReadonlySet<string> }): { summary: string; results: AiV2Result[] } | null {
  if (!isRec(raw) || !Array.isArray(raw.results)) return null;
  const results: AiV2Result[] = [];
  for (const item of raw.results.slice(0, AI_V2_LIMITS.maxResults)) {
    const result = normalizeAiV2Result(item, allowed, `r${results.length + 1}`);
    if (result) results.push(result);
  }
  return { summary: str(raw.summary).trim(), results };
}

export type AiV2RequestCheck = { ok: true; request: AiV2AnyRequest } | { ok: false; code: AiV2ErrorCode; message: string };

// Validates an incoming request body (the function) or an outgoing one (the client, before upload).
export function checkAiV2Request(raw: unknown): AiV2RequestCheck {
  if (!isRec(raw)) return { ok: false, code: 'badRequest', message: 'request body is not an object' };
  if (raw.protocolVersion !== AI_V2_PROTOCOL_VERSION) return { ok: false, code: 'badRequest', message: `unsupported protocolVersion ${String(raw.protocolVersion)}` };
  if (raw.kind === 'reportWording') return checkWordingRequest(raw);
  if (raw.kind === 'poolSort') return checkSortRequest(raw);
  if (raw.kind !== 'area') return { ok: false, code: 'badRequest', message: 'kind must be area, reportWording or poolSort' };
  if (typeof raw.requestId !== 'string' || raw.requestId.length === 0 || raw.requestId.length > 80) return { ok: false, code: 'badRequest', message: 'requestId missing' };
  if (!isAiV2Locale(raw.uiLocale) || !isAiV2Locale(raw.reportLocale)) return { ok: false, code: 'badRequest', message: 'uiLocale / reportLocale must be zh-CN or en-US' };
  if (!isRec(raw.area) || typeof raw.area.areaId !== 'string' || typeof raw.area.areaName !== 'string') return { ok: false, code: 'badRequest', message: 'area context missing' };
  if (!Array.isArray(raw.checklist) || raw.checklist.length > AI_V2_LIMITS.maxChecklistItems) return { ok: false, code: 'badRequest', message: 'checklist missing or too long' };
  if (!Array.isArray(raw.photos) || raw.photos.length === 0) return { ok: false, code: 'badRequest', message: 'at least one photo is required' };
  if (raw.photos.length > AI_V2_LIMITS.maxPhotos) return { ok: false, code: 'tooManyImages', message: `at most ${AI_V2_LIMITS.maxPhotos} photos per request` };
  const scanNote = str(raw.scanNote);
  if (scanNote.length > AI_V2_LIMITS.maxNoteChars) return { ok: false, code: 'badRequest', message: 'scanNote too long' };
  const photos: AiV2Photo[] = [];
  const seen = new Set<string>();
  for (const p of raw.photos) {
    if (!isRec(p) || typeof p.id !== 'string' || p.id.length === 0 || seen.has(p.id)) return { ok: false, code: 'badRequest', message: 'photo id missing or duplicated' };
    if (!isAiV2MediaType(p.mediaType)) return { ok: false, code: 'unsupportedImage', message: `unsupported image type ${String(p.mediaType)}` };
    if (typeof p.base64 !== 'string' || p.base64.length === 0) return { ok: false, code: 'unsupportedImage', message: 'photo bytes missing' };
    if (p.base64.length > AI_V2_LIMITS.maxPhotoBase64Chars) return { ok: false, code: 'tooLarge', message: 'one photo exceeds the size limit' };
    const note = str(p.note);
    if (note.length > AI_V2_LIMITS.maxNoteChars) return { ok: false, code: 'badRequest', message: 'photo note too long' };
    seen.add(p.id);
    photos.push({ id: p.id, mediaType: p.mediaType, base64: p.base64, note, capturedAt: typeof p.capturedAt === 'number' ? p.capturedAt : null });
  }
  const checklist: AiV2ChecklistItem[] = [];
  for (const c of raw.checklist) {
    if (!isRec(c) || typeof c.id !== 'string' || typeof c.title !== 'string') return { ok: false, code: 'badRequest', message: 'checklist item malformed' };
    checklist.push({
      id: c.id,
      title: c.title,
      titleEn: strOrNull(c.titleEn),
      hint: str(c.hint),
      status: str(c.status, 'notChecked'),
      templateId: strOrNull(c.templateId)?.slice(0, 80) ?? null,
      ...('trade' in c ? { trade: c.trade === null ? null : typeof c.trade === 'string' && AI_V2_TRADES.includes(c.trade) ? c.trade : null } : {})
    });
  }
  const area: AiV2AreaContext = {
    areaId: raw.area.areaId,
    areaName: raw.area.areaName,
    areaNameEn: strOrNull(raw.area.areaNameEn),
    areaKind: str(raw.area.areaKind, 'other'),
    floor: typeof raw.area.floor === 'number' ? raw.area.floor : null,
    propertyType: strOrNull(raw.area.propertyType),
    yearBuilt: typeof raw.area.yearBuilt === 'number' ? raw.area.yearBuilt : null,
    // 2026-10-18: the recorded house systems (null = not sent / nothing recorded).
    house: checkAiV2HouseContext(raw.area.house)
  };
  const inspectionId = typeof raw.inspectionId === 'string' && raw.inspectionId.length > 0 && raw.inspectionId.length <= 120 ? raw.inspectionId : null;
  const photoGuide = checkAiV2PhotoGuide(raw.photoGuide);
  return { ok: true, request: { protocolVersion: AI_V2_PROTOCOL_VERSION, kind: 'area', requestId: raw.requestId, uiLocale: raw.uiLocale, reportLocale: raw.reportLocale, area, checklist, scanNote, photos, inspectionId, ...(photoGuide ? { photoGuide } : {}) } };
}

// The allowance block, validated. Absent or off-contract = "the server said nothing about money",
// never invented numbers.
export function checkAiV2Quota(raw: unknown): AiV2Quota | null {
  if (!isRec(raw)) return null;
  const number = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : 0);
  return {
    remainingMicros: number(raw.remainingMicros),
    inspectionRemainingMicros: typeof raw.inspectionRemainingMicros === 'number' && Number.isFinite(raw.inspectionRemainingMicros) ? Math.max(0, raw.inspectionRemainingMicros) : null,
    remainingInspections: number(raw.remainingInspections),
    planActive: raw.planActive === true,
    canBuyAddOn: raw.canBuyAddOn === true
  };
}

// Client-side check of an answer: everything the UI relies on must be present and typed.
export function checkAiV2Response(raw: unknown): AiV2Response | null {
  if (!isRec(raw) || raw.protocolVersion !== AI_V2_PROTOCOL_VERSION) return null;
  const quota = 'quota' in raw ? checkAiV2Quota(raw.quota) : null;
  if (raw.ok === false) {
    const code = str(raw.code) as AiV2ErrorCode;
    if (!(code in AI_V2_ERROR_STATUS)) return null;
    const error: AiV2ErrorResponse = { ok: false, protocolVersion: AI_V2_PROTOCOL_VERSION, requestId: strOrNull(raw.requestId), code, message: str(raw.message), retryable: raw.retryable === true };
    if (quota) error.quota = quota;
    return error;
  }
  if (raw.ok !== true) return null;
  if (raw.kind === 'reportWording') {
    if (typeof raw.requestId !== 'string' || !Array.isArray(raw.rows) || !isRec(raw.usage)) return null;
    const rows: AiV2WordingSentence[] = [];
    for (const r of raw.rows) {
      if (!isRec(r) || typeof r.itemId !== 'string') return null;
      rows.push({ itemId: r.itemId, en: str(r.en), zh: str(r.zh) });
    }
    const wu = raw.usage;
    const wording: AiV2WordingSuccessResponse = {
      ok: true,
      protocolVersion: AI_V2_PROTOCOL_VERSION,
      kind: 'reportWording',
      requestId: raw.requestId,
      rows,
      areaSummary: isRec(raw.areaSummary) ? { en: str(raw.areaSummary.en), zh: str(raw.areaSummary.zh) } : { en: '', zh: '' },
      usage: {
        provider: 'anthropic',
        model: str(wu.model),
        inputTokens: typeof wu.inputTokens === 'number' ? wu.inputTokens : 0,
        outputTokens: typeof wu.outputTokens === 'number' ? wu.outputTokens : 0,
        durationMs: typeof wu.durationMs === 'number' ? wu.durationMs : 0,
        requestId: str(wu.requestId, raw.requestId),
        stopReason: strOrNull(wu.stopReason)
      },
      warnings: strArray(raw.warnings, 20)
    };
    if (quota) wording.quota = quota;
    return wording;
  }
  if (raw.kind === 'poolSort') {
    if (typeof raw.requestId !== 'string' || !Array.isArray(raw.placements) || !isRec(raw.usage)) return null;
    const placements: AiV2Placement[] = [];
    for (const p of raw.placements) {
      if (!isRec(p) || typeof p.photoId !== 'string') return null;
      placements.push({
        photoId: p.photoId,
        areaId: strOrNull(p.areaId),
        itemIds: strArray(p.itemIds, AI_V2_SORT_LIMITS.maxRowsPerPhoto),
        confidence: p.confidence === 'low' || p.confidence === 'medium' || p.confidence === 'high' ? p.confidence : 'low'
      });
    }
    const su = raw.usage;
    const sorted: AiV2SortSuccessResponse = {
      ok: true,
      protocolVersion: AI_V2_PROTOCOL_VERSION,
      kind: 'poolSort',
      requestId: raw.requestId,
      placements,
      usage: {
        provider: 'anthropic',
        model: str(su.model),
        inputTokens: typeof su.inputTokens === 'number' ? su.inputTokens : 0,
        outputTokens: typeof su.outputTokens === 'number' ? su.outputTokens : 0,
        durationMs: typeof su.durationMs === 'number' ? su.durationMs : 0,
        requestId: str(su.requestId, raw.requestId),
        stopReason: strOrNull(su.stopReason)
      },
      warnings: strArray(raw.warnings, 20)
    };
    if (quota) sorted.quota = quota;
    return sorted;
  }
  if (raw.kind !== 'area' || typeof raw.requestId !== 'string' || !Array.isArray(raw.results) || !isRec(raw.usage)) return null;
  const results: AiV2Result[] = [];
  for (const r of raw.results) {
    if (!isRec(r) || typeof r.id !== 'string' || !isAiV2ResultType(r.type)) return null;
    const observation = str(r.observation);
    const observationUi = strOrNull(r.observationUi);
    const headline = str(r.headline);
    const guidance = str(r.guidance);
    results.push({
      id: r.id,
      type: r.type,
      itemId: strOrNull(r.itemId),
      title: str(r.title),
      headline,
      observation,
      concern: str(r.concern),
      guidance,
      checkSteps: strArray(r.checkSteps, 20),
      recommendation: str(r.recommendation),
      observationUi,
      recommendationUi: strOrNull(r.recommendationUi),
      responsibleTrade: typeof r.responsibleTrade === 'string' && AI_V2_TRADES.includes(r.responsibleTrade) ? r.responsibleTrade : null,
      confidence: r.confidence === 'low' || r.confidence === 'medium' || r.confidence === 'high' ? r.confidence : 'low',
      // 2026-10-18: a server from before the field sends none — the client derives it the same way.
      verdict: isAiV2Verdict(r.verdict) ? r.verdict : deriveAiV2Verdict({ type: r.type, observation, observationUi, headline, guidance }),
      question: strOrNull(r.question),
      scopeNote: strOrNull(r.scopeNote),
      photoIds: strArray(r.photoIds, AI_V2_LIMITS.maxPhotos)
    });
  }
  const u = raw.usage;
  const usage: AiV2Usage = {
    provider: 'anthropic',
    model: str(u.model),
    inputTokens: typeof u.inputTokens === 'number' ? u.inputTokens : 0,
    outputTokens: typeof u.outputTokens === 'number' ? u.outputTokens : 0,
    durationMs: typeof u.durationMs === 'number' ? u.durationMs : 0,
    requestId: str(u.requestId, raw.requestId),
    stopReason: strOrNull(u.stopReason)
  };
  const success: AiV2SuccessResponse = { ok: true, protocolVersion: AI_V2_PROTOCOL_VERSION, kind: 'area', requestId: raw.requestId, summary: str(raw.summary), results, usage, warnings: strArray(raw.warnings, 20) };
  if (quota) success.quota = quota;
  return success;
}
